import { useRef, useState } from 'react';

/**
 * Close-on-scrim-click that only fires when the press STARTED on the scrim
 * itself. A click is dispatched on the common ancestor of mousedown and
 * mouseup targets — so selecting text inside a sheet and releasing over the
 * backdrop lands the click on the scrim and would dismiss it. Tracking the
 * pointerdown origin kills that whole class of accidental closes while
 * keeping intentional backdrop taps working.
 *
 * Usage: const scrim = useScrimClose(() => setOpen(false));
 *        <div className="sheet-scrim" role="presentation" {...scrim}>
 */
export const useScrimClose = (onClose: () => void) => {
  const armedRef = useRef(false);
  return {
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
      armedRef.current = event.target === event.currentTarget;
    },
    onClick: (event: React.MouseEvent<HTMLElement>) => {
      const armed = armedRef.current;
      armedRef.current = false;
      if (armed && event.target === event.currentTarget) {
        onClose();
      }
    },
  };
};

/**
 * The same backdrop close, but a sheet the user has already typed into is never
 * thrown away by a single stray tap next to it: the attempt raises `asking`,
 * and the screen renders a ConfirmSheet on top of it.
 *
 * Usage: const guard = useDiscardGuard(() => setOpen(false), title !== '');
 *        <div className="sheet-scrim" {...guard.scrim}>…</div>
 *        {guard.asking ? <ConfirmSheet … onConfirm={guard.discard}
 *                                        onCancel={guard.keep} /> : null}
 */
export const useDiscardGuard = (close: () => void, dirty: boolean) => {
  const [asking, setAsking] = useState(false);

  const attempt = () => {
    if (dirty) {
      setAsking(true);
      return;
    }
    close();
  };

  return {
    scrim: useScrimClose(attempt),
    /** Call for any other dismissal route (drag-to-dismiss, Zrušiť, Escape). */
    attempt,
    asking,
    discard: () => {
      setAsking(false);
      close();
    },
    keep: () => setAsking(false),
  };
};
